/**
 * Plaid 试链窗口态（be-design v5.0 · listConnections 每条 connection 的 `plaid_status`）。
 * OpenAPI 未生成前临时扩展；取值仅 `trial` | `expired`（纯时间，不含付费）。
 */
export type PlaidStatus = "trial" | "expired";

export const PLAID_STATUS_TRIAL: PlaidStatus = "trial";
export const PLAID_STATUS_EXPIRED: PlaidStatus = "expired";

/** Connection 级字段（与 `status` 同级）；`data=[]` 时不存在 */
export type WithPlaidStatus = {
  plaid_status?: PlaidStatus | string | null;
};

/**
 * 从 listConnections 结果派生 owner 级试链窗口。
 * - 空列表 → `null`（未连接，无字段）
 * - 任一条 `expired` → `expired`（同 owner 多连接同值）
 * - 否则取首个合法 `trial` / `expired`
 */
export function resolvePlaidStatusFromConnections(
  connections: ReadonlyArray<WithPlaidStatus> | null | undefined,
): PlaidStatus | null {
  const list = connections ?? [];
  if (list.length === 0) {
    return null;
  }

  let sawTrial = false;
  for (const conn of list) {
    const status = normalizePlaidStatus(conn.plaid_status);
    if (status === PLAID_STATUS_EXPIRED) {
      return PLAID_STATUS_EXPIRED;
    }
    if (status === PLAID_STATUS_TRIAL) {
      sawTrial = true;
    }
  }

  return sawTrial ? PLAID_STATUS_TRIAL : null;
}

export function normalizePlaidStatus(value: unknown): PlaidStatus | null {
  if (value === PLAID_STATUS_TRIAL) {
    return PLAID_STATUS_TRIAL;
  }
  if (value === PLAID_STATUS_EXPIRED) {
    return PLAID_STATUS_EXPIRED;
  }
  return null;
}

/**
 * 到期提示 / 冻结判定（be-design §4.6）：
 * 先会员态（已付费全覆盖），再 `plaid_status===expired`。
 * 未连接（无 status）→ 不展示到期 Banner。
 */
export function resolvePlaidTrialExpiredUi(params: {
  isPaidMember: boolean;
  plaidStatus: PlaidStatus | null;
}): boolean {
  if (params.isPaidMember) {
    return false;
  }
  return params.plaidStatus === PLAID_STATUS_EXPIRED;
}

/**
 * 新建连接是否先弹 Paywall（PRD US1 / be-design §4.6）。
 * - 已付费 → 否
 * - `paywall_first` → 是
 * - `free_trial` → 仅 `plaid_status=expired`（免费期已用完）才弹；
 *   未连接 (`null`) / `trial` → 跳过 Paywall，直接进 Plaid
 */
export function shouldRequirePlaidConnectPaywall(params: {
  isPaidMember: boolean;
  gateMode: "free_trial" | "paywall_first";
  plaidStatus: PlaidStatus | null;
}): boolean {
  if (params.isPaidMember) {
    return false;
  }
  if (params.gateMode === "paywall_first") {
    return true;
  }
  return params.plaidStatus === PLAID_STATUS_EXPIRED;
}

/**
 * 「Free for 30 days」角标（PRD：free_trial ∧ 未付费 ∧ 免费期未用完）。
 * 未连接 (`null`) / `trial` → 显示；`expired` / 已付费 / 非 free_trial → 隐藏。
 */
export function shouldShowPlaidFreeTrialBadge(params: {
  isPaidMember: boolean;
  gateMode: "free_trial" | "paywall_first" | null | undefined;
  plaidStatus: PlaidStatus | null;
}): boolean {
  if (params.isPaidMember) {
    return false;
  }
  if (params.gateMode !== "free_trial") {
    return false;
  }
  return params.plaidStatus !== PLAID_STATUS_EXPIRED;
}
